import { execSQL, esc } from './api';

/* ------------------------------------------------------------------
 * Aggregate queries behind the Dashboard and Reports pages.
 * The API returns only the first result set, so anything that needs
 * several numbers at once is written as one row of scalar subqueries.
 * ------------------------------------------------------------------ */

// "Resigned" is derived from the resignation date, the same rule the
// employee list uses — the status column only marks soft-deletes.
export const fetchDashboardStats = () =>
  execSQL(`
    SELECT
      (SELECT COUNT(*) FROM dbo.payroll_employee
        WHERE status <> 'Deleted'
          AND (dateOfResignation IS NULL OR dateOfResignation > GETDATE()))                     AS activeEmployees,
      (SELECT COUNT(*) FROM dbo.payroll_employee
        WHERE status <> 'Deleted'
          AND dateOfResignation IS NOT NULL AND dateOfResignation <= GETDATE())                 AS resignedEmployees,
      (SELECT COUNT(*) FROM dbo.payroll_leave    WHERE status = 'Pending')                      AS pendingLeave,
      (SELECT COUNT(*) FROM dbo.payroll_loan     WHERE status = 'Active')                       AS activeLoans,
      (SELECT ISNULL(SUM(remaining), 0) FROM dbo.payroll_loan WHERE status = 'Active')          AS loanOutstanding,
      (SELECT COUNT(*) FROM dbo.payroll_salary_component WHERE status <> 'Deleted')             AS componentCount
  `);

export const fetchRunSummary = (limit = 6) =>
  execSQL(`
    SELECT TOP ${Number(limit)} id, runMonth, status, employeeCount,
           totalGross, totalDeductions, totalEpfEmployee, totalEpfEmployer,
           totalEtf, totalNet, calculatedAt
    FROM dbo.payroll_run
    WHERE status <> 'Deleted'
    ORDER BY runMonth DESC
  `);

export const fetchPayrollSummaryReport = () =>
  execSQL(`
    SELECT runMonth, status, employeeCount, totalGross, totalDeductions,
           totalEpfEmployee, totalEpfEmployer, totalEtf, totalNet
    FROM dbo.payroll_run
    WHERE status <> 'Deleted'
    ORDER BY runMonth DESC
  `);

export const fetchTaxReport = (month) =>
  execSQL(`
    SELECT emp_id, empName, category, grossPay, paye
    FROM dbo.payroll_run_detail
    WHERE runMonth = ${esc(month)} AND paye > 0
    ORDER BY paye DESC
  `);

export const fetchEpfEtfReport = (month) =>
  execSQL(`
    SELECT emp_id, empName, basicSalary, budgetaryAllowance,
           epfEmployee, epfEmployer, etf
    FROM dbo.payroll_run_detail
    WHERE runMonth = ${esc(month)} AND (epfEmployee > 0 OR epfEmployer > 0)
    ORDER BY CAST(emp_id AS INT)
  `);

export const fetchLoanReport = () =>
  execSQL(`
    SELECT l.emp_id,
           LTRIM(RTRIM(ISNULL(e.initial,'') + ' ' + ISNULL(e.firstName,'') + ' ' + ISNULL(e.surName,''))) AS empName,
           l.loanType, l.principal, l.interestRate, l.totalPayable,
           l.installments, l.monthlyInstallment, l.remaining, l.status,
           CONVERT(varchar(10), l.startDate, 120) AS startDate
    FROM dbo.payroll_loan l
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = l.emp_id
    ORDER BY l.status, l.id DESC
  `);

export const fetchLeaveReport = () =>
  execSQL(`
    SELECT lv.emp_id,
           LTRIM(RTRIM(ISNULL(e.initial,'') + ' ' + ISNULL(e.firstName,'') + ' ' + ISNULL(e.surName,''))) AS empName,
           lv.leaveType, lv.days, lv.status,
           CONVERT(varchar(10), lv.dateFrom, 120) AS dateFrom,
           CONVERT(varchar(10), lv.dateTo,   120) AS dateTo,
           lv.reason
    FROM dbo.payroll_leave lv
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = lv.emp_id
    ORDER BY lv.id DESC
  `);

export const fetchPayrollDetailReport = (month) =>
  execSQL(`
    SELECT emp_id, empName, category, basicSalary, noPay, noPayDays, budgetaryAllowance,
           increment, otherAllowance, grossPay, stampDuty, paye, loanDeduction,
           advanceDeduction, ISNULL(festivalAdvance,0) AS festivalAdvance,
           ISNULL(insuranceDeduction,0) AS insuranceDeduction,
           ISNULL(otherDeduction,0) AS otherDeduction,
           epfEmployee, epfEmployer, etf, totalDeductions, netPay,
           ISNULL(attendanceAllowance,0) AS attendanceAllowance
    FROM dbo.payroll_run_detail
    WHERE runMonth = ${esc(month)}
    ORDER BY CAST(emp_id AS INT)
  `);

/* ---- Statutory statements ----------------------------------------
 * These match the client's own upload files column-for-column:
 *   EPF June 2026.xlsm                      (15 columns)
 *   ETF Contribution File Generator .xlsm   (8 columns)
 * Employer constants come from their files: EPF employer 22016 / zone A,
 * ETF employer "A 022016". Days worked is 30 less any no-pay days.
 * NIC is the fund-registered one — some staff have since been issued a new
 * NIC, but the funds still transfer against the number they hold. EPF and ETF
 * are registered separately and differ for a handful of members, so each
 * statement uses its own fund's details.
 * ------------------------------------------------------------------ */

export const EPF_EMPLOYER_NO = '22016';
export const EPF_ZONE        = 'A';
export const ETF_EMPLOYER_NO = 'A 022016';

// EPF upload format. Surname and initials are the EPF-registered ones,
// kept as separate columns exactly as the fund expects them.
export const fetchEpfStatement = (month) => {
  const period = String(month).replace('-', '');            // 2026-06 -> 202606
  return execSQL(`
    SELECT ISNULL(e.epfNic, e.nic)                 AS nicNumber,
           ISNULL(e.epfSurname, e.surName)         AS surname,
           ISNULL(e.epfInitials, e.initials)       AS initials,
           d.emp_id                                AS memberNumber,
           (d.epfEmployee + d.epfEmployer)         AS totalContribution,
           d.epfEmployer                           AS employerContribution,
           d.epfEmployee                           AS memberContribution,
           (ISNULL(d.basicSalary,0) - ISNULL(d.noPay,0)
              + ISNULL(d.budgetaryAllowance,0) + ISNULL(d.increment,0)) AS totalEarnings,
           ISNULL(e.epfMemberStatus, 'E')          AS memberStatus,
           '${EPF_ZONE}'                           AS zone,
           '${EPF_EMPLOYER_NO}'                    AS employerNumber,
           '${period}'                             AS contributionPeriod,
           1                                       AS dataSubmissionNumber,
           (30 - ISNULL(d.noPayDays, 0))           AS daysWorked,
           e.occupationGrade                       AS occupationGrade
    FROM dbo.payroll_run_detail d
    INNER JOIN dbo.payroll_run r ON r.id = d.runId
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = d.emp_id
    WHERE d.runMonth = ${esc(month)} AND r.status <> 'Deleted' AND d.epfEmployee > 0
    ORDER BY CAST(d.emp_id AS INT)
  `);
};

// ETF upload format. Member number is zero-padded to 6 digits and the
// contribution period is the first and last day of the month.
export const fetchEtfStatement = (month) => {
  const [y, m] = String(month).split('-').map(Number);
  const from = `${y}/${String(m).padStart(2, '0')}/01`;
  const last = new Date(y, m, 0).getDate();
  const to   = `${y}/${String(m).padStart(2, '0')}/${String(last).padStart(2, '0')}`;
  return execSQL(`
    SELECT ISNULL(e.etfNic, ISNULL(e.epfNic, e.nic))            AS nicNumber,
           ISNULL(e.etfSurname, ISNULL(e.epfSurname, e.surName))  AS surname,
           ISNULL(e.etfInitials, ISNULL(e.epfInitials, e.initials)) AS initials,
           RIGHT('000000' + CAST(d.emp_id AS VARCHAR(10)), 6) AS memberNumber,
           d.etf                             AS totalContribution,
           '${ETF_EMPLOYER_NO}'              AS employerNumber,
           '${from}'                         AS periodFrom,
           '${to}'                           AS periodTo
    FROM dbo.payroll_run_detail d
    INNER JOIN dbo.payroll_run r ON r.id = d.runId
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = d.emp_id
    WHERE d.runMonth = ${esc(month)} AND r.status <> 'Deleted' AND d.etf > 0
    ORDER BY CAST(d.emp_id AS INT)
  `);
};

export const fetchStampDutyStatement = (month) =>
  execSQL(`
    SELECT emp_id, empName, category, grossPay, stampDuty
    FROM dbo.payroll_run_detail d
    WHERE d.runMonth = ${esc(month)} AND d.stampDuty > 0
    ORDER BY CAST(emp_id AS INT)
  `);

export const fetchPayeStatement = (month) =>
  execSQL(`
    SELECT d.emp_id, d.empName, e.nic, d.category, d.grossPay, d.paye
    FROM dbo.payroll_run_detail d
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = d.emp_id
    WHERE d.runMonth = ${esc(month)} AND d.paye > 0
    ORDER BY d.paye DESC
  `);

/* ---- Analysis ---- */

// Current month vs the month before, per employee.
export const fetchMonthComparison = (month) => {
  const [y, m] = String(month).split('-').map(Number);
  const prevDate = new Date(y, m - 2, 1);
  const prev = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
  return execSQL(`
    SELECT ISNULL(c.emp_id, p.emp_id)   AS emp_id,
           ISNULL(c.empName, p.empName) AS empName,
           ISNULL(p.grossPay, 0)        AS prevGross,
           ISNULL(c.grossPay, 0)        AS currGross,
           (ISNULL(c.grossPay,0) - ISNULL(p.grossPay,0)) AS difference,
           ISNULL(p.netPay, 0)          AS prevNet,
           ISNULL(c.netPay, 0)          AS currNet
    FROM      (SELECT * FROM dbo.payroll_run_detail WHERE runMonth = ${esc(month)}) c
    FULL JOIN (SELECT * FROM dbo.payroll_run_detail WHERE runMonth = ${esc(prev)})  p
           ON c.emp_id = p.emp_id
    ORDER BY ABS(ISNULL(c.grossPay,0) - ISNULL(p.grossPay,0)) DESC
  `);
};

// Per-employee allowance breakdown by component (Club & Societies, Sports, Form Teacher…).
export const fetchAllowanceBreakdown = () =>
  execSQL(`
    SELECT s.componentName,
           COUNT(*)              AS employeeCount,
           SUM(s.value)          AS totalAmount,
           AVG(s.value)          AS averageAmount
    FROM dbo.payroll_salary_structure s
    WHERE s.status <> 'Deleted' AND s.type = 'Allowance'
    GROUP BY s.componentName
    ORDER BY SUM(s.value) DESC
  `);

// Allowance totals grouped by school category.
export const fetchCategoryAllowanceReport = () =>
  execSQL(`
    SELECT ISNULL(e.category, 'Unassigned') AS category,
           COUNT(DISTINCT s.emp_id)         AS employees,
           SUM(s.value)                     AS totalAllowance
    FROM dbo.payroll_salary_structure s
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = s.emp_id
    WHERE s.status <> 'Deleted' AND s.type = 'Allowance'
    GROUP BY e.category
    ORDER BY SUM(s.value) DESC
  `);

/* Contract staff with their age, for the start-of-year renewal review.
 * The client asked for this so contracts can be checked against age. */
export const fetchContractRenewalReport = () =>
  execSQL(`
    SELECT emp_id,
           LTRIM(RTRIM(ISNULL(initial,'') + ' ' + ISNULL(firstName,'') + ' ' + ISNULL(surName,''))) AS empName,
           category, employmentType,
           CONVERT(varchar(10), dob, 120)           AS dob,
           DATEDIFF(YEAR, dob, GETDATE())
             - CASE WHEN DATEADD(YEAR, DATEDIFF(YEAR, dob, GETDATE()), dob) > GETDATE()
                    THEN 1 ELSE 0 END               AS age,
           CONVERT(varchar(10), dateOfJoining, 120) AS dateOfJoining,
           basicSalary
    FROM dbo.payroll_employee
    WHERE status <> 'Deleted'
      AND (dateOfResignation IS NULL OR dateOfResignation > GETDATE())
      AND employmentType <> 'Permanent'
    ORDER BY age DESC, CAST(emp_id AS INT)
  `);

/* ---------------- CSV export (client-side, no backend needed) ---------------- */

export const toCSV = (rows, columns) => {
  if (!rows?.length) return '';
  const cols = columns ?? Object.keys(rows[0]).map((k) => ({ key: k, label: k }));
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = cols.map((c) => escape(c.label)).join(',');
  const body = rows.map((r) => cols.map((c) => escape(r[c.key])).join(',')).join('\n');
  return `${head}\n${body}`;
};

export const downloadCSV = (filename, rows, columns) => {
  const csv = toCSV(rows, columns);
  if (!csv) return false;
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return true;
};
